import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit, Trash2, Calendar, Clock, BarChart2, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import toast from 'react-hot-toast';

const Muhurats = () => {
    const navigate = useNavigate();
    const [muhurats, setMuhurats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [monthFilter, setMonthFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedMuhurat, setSelectedMuhurat] = useState(null);
    const [stats, setStats] = useState({
        totalMuhurats: 0,
        upcomingMuhurats: 0,
        thisMonthCount: 0
    });

    const months = ['All', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    useEffect(() => {
        fetchMuhurats();
        fetchStats();
    }, []);

    const fetchMuhurats = async () => {
        try {
            setLoading(true);
            const response = await ApiService.getAllMuhurats();
            if (response.success) {
                setMuhurats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching muhurats:', error);
            toast.error('Failed to fetch muhurats');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await ApiService.getMuhuratStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleDelete = (muhurat) => {
        setSelectedMuhurat(muhurat);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const response = await ApiService.deleteMuhurat(selectedMuhurat.id);
            if (response.success) {
                toast.success('Muhurat deleted successfully');
                fetchMuhurats();
                fetchStats();
                setIsDeleteModalOpen(false);
            }
        } catch (error) {
            console.error('Error deleting muhurat:', error);
            toast.error('Failed to delete muhurat');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('hi-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const filteredMuhurats = muhurats.filter(m => {
        const matchesSearch = searchTerm === '' ||
            m.day.includes(searchTerm) ||
            formatDate(m.date).includes(searchTerm);

        // Simple month matching - could be improved by checking m.date
        const matchesMonth = monthFilter === 'All' || m.month === monthFilter;

        const matchesStatus = statusFilter === 'All' ||
            (statusFilter === 'Active' ? m.isActive : !m.isActive);

        return matchesSearch && matchesMonth && matchesStatus;
    });

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Lagna Muhurat</h1>
                    <p className="text-gray-500 mt-2 text-lg">Manage auspicious wedding timings and astrology details</p>
                </div>
                <button
                    onClick={() => navigate('/muhurats/add')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg shadow-indigo-200 transition-all transform hover:-translate-y-1"
                >
                    <Plus className="w-5 h-5" />
                    Add New Muhurat
                </button>
            </div>

            {/* Stats Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-xl">
                        <BarChart2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Total Muhurats</p>
                        <h3 className="text-2xl font-bold text-gray-900">{stats.totalMuhurats}</h3>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-green-50 rounded-xl">
                        <Calendar className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Upcoming This Month</p>
                        <h3 className="text-2xl font-bold text-gray-900">{stats.thisMonthCount}</h3>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-purple-50 rounded-xl">
                        <Clock className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Total Upcoming</p>
                        <h3 className="text-2xl font-bold text-gray-900">{stats.upcomingMuhurats}</h3>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by date or day..."
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <select
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                >
                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                </select>
                <div className="flex items-center justify-center text-gray-500 font-medium">
                    <Filter className="w-4 h-4 mr-2" />
                    {filteredMuhurats.length} Results
                </div>
            </div>

            {/* Muhurats Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-gray-100 animate-pulse h-64 rounded-2xl" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMuhurats.map((muhurat) => (
                        <div key={muhurat.id} className="bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all border border-gray-100 group overflow-hidden">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${muhurat.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                        {muhurat.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => navigate(`/muhurats/edit/${muhurat.id}`, { state: { muhurat } })}
                                            className="p-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(muhurat)}
                                            className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 mb-2">
                                    <Calendar className="w-5 h-5 text-indigo-600" />
                                    <h3 className="text-xl font-bold text-gray-900">{formatDate(muhurat.date)}</h3>
                                </div>
                                <p className="text-indigo-600 font-bold mb-4 ml-8">{muhurat.day}</p>

                                <div className="space-y-3 mb-6">
                                    <div className="flex items-center gap-3 text-gray-600">
                                        <Clock className="w-4 h-4" />
                                        <span className="text-sm font-medium">{muhurat.startTime} – {muhurat.endTime}</span>
                                    </div>
                                    {muhurat.tithi && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs bg-gray-100 px-3 py-1 rounded-lg text-gray-600 font-bold">Tithi: {muhurat.tithi}</span>
                                            <span className="text-xs bg-gray-100 px-3 py-1 rounded-lg text-gray-600 font-bold">Nakshatra: {muhurat.nakshatra}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-4 border-t border-gray-50">
                                    <p className="text-gray-500 text-sm line-clamp-2 italic">
                                        {muhurat.description || 'Auspicious Lagna Muhurat for marriage ceremonies.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredMuhurats.length === 0 && (
                        <div className="col-span-full py-20 text-center">
                            <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Calendar className="w-10 h-10 text-gray-300" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900">No Muhurats Found</h3>
                            <p className="text-gray-500">Try adjusting your filters or search terms.</p>
                        </div>
                    )}
                </div>
            )}

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Muhurat"
                message={`Are you sure you want to delete the muhurat for ${selectedMuhurat && formatDate(selectedMuhurat.date)}? This action cannot be undone.`}
            />
        </div>
    );
};

export default Muhurats;
